import { CONFIG_ELEMENTS, type RichTextPanelItem } from '@vedalib/editor/lib/elements'
import { rtValueToText } from '@vedalib/rich-text'
import { useMount } from 'ahooks'
import lodash from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'

import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'
import { PreviewMode } from 'services/Store/Project/enums'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'

import { useElementCompleted } from '../../hooks/useElementCompleted'
import useSetActiveItemElement from '../../hooks/useSetActiveItemElement'
import AccordionElementItem from './AccordionElementItem'
import type { AccordionElementType } from './accordionTypes'
import RootStyled from './styled/RootStyled'

const toMap = (items: RichTextPanelItem[]) =>
  items.reduce<Record<string, boolean>>((acc, item) => ({ ...acc, [item.value]: false }), {})

export enum Position {
  left = 'left',
  right = 'right',
}

const AccordionElement: AccordionElementType = ({
  block,
  element,
  styles,
  font,
  mode,
  state,
  waiting,
  setState,
  onChange,
}) => {
  const { items, multipleExpand = true, showFirstTab = false } = element.value
  const { id: elementId } = element
  const [viewedTabs, setViewedTabs] = useState(() => toMap(items))
  const allViewed = lodash.every(viewedTabs)

  const handleChoose = useCallback((value: string) => setState?.({ active: value }), [setState])
  const { isActiveElement, activeIndex, onLabelSelect } = useGetRichTextProps({ elementId, mode })
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.accordion.type,
      elementId,
    },
  )

  const handleToggle = useCallback(
    (value: string) => {
      const isOpen = !state?.open?.[value]
      const itemIndex = items.findIndex((i) => i.value === value)
      const itemLabel = rtValueToText(items.find((i) => i.value === value)?.label || [])
      const itemNumber = itemIndex + 1
      const wordCount = itemLabel.length
      const collapseData = entityAnalyticsConfig.collapsed({
        resultExtensions: { itemIndex, itemLabel },
        objectExtensions: { itemNumber, wordCount, itemId: value, ...baseObjectExtensions },
        elementId,
      })
      const expandData = entityAnalyticsConfig.expanded({
        resultExtensions: { itemIndex, itemLabel },
        objectExtensions: { itemNumber, wordCount, itemId: value, ...baseObjectExtensions },
        elementId,
      })
      sendXapiData(isOpen ? expandData : collapseData)
      // sendXapiData(data)
      if (multipleExpand) {
        setState?.({ open: { ...state?.open, [value]: isOpen } })
      } else {
        setState?.({ open: { [value]: isOpen } })
      }

      if (!allViewed) {
        setViewedTabs((prev) => ({ ...prev, [value]: true }))
      }
    },
    [
      state?.open,
      items,
      entityAnalyticsConfig,
      baseObjectExtensions,
      elementId,
      sendXapiData,
      multipleExpand,
      allViewed,
      setState,
    ],
  )

  useMount(() => {
    if (showFirstTab) {
      setState?.({ open: { ...state?.open, [items[0].value]: true } })
      setViewedTabs((prev) => ({ ...prev, [items[0].value]: true }))
    }
  })

  const setActiveItem = useCallback(
    (value: string) => setState?.({ open: { ...state?.open, [value]: true } }),
    [setState, state?.open],
  )

  useSetActiveItemElement(items, block, setActiveItem)
  useElementCompleted({
    block,
    elementId: element.id,
    completed: allViewed,
    deviceMode: mode.deviceMode,
  })

  useEffect(() => {
    if (!state?.open) {
      return
    }

    setViewedTabs((prev) =>
      lodash.reduce(
        state.open,
        (acc, value, key) => {
          if (value) {
            acc[key] = true
          }
          return acc
        },
        { ...prev },
      ),
    )
  }, [state?.open])

  if (mode.previewMode === PreviewMode.pdf) {
    return (
      <RootStyled $styles={styles}>
        {items.map((item, index) => (
          <AccordionElementItem
            block={block}
            element={element}
            font={font}
            index={index}
            item={item}
            key={item.value}
            mode={mode}
            styles={styles}
            isOpen
          />
        ))}
      </RootStyled>
    )
  }

  return (
    <RootStyled $styles={styles}>
      {items.map((item, index) => (
        <AccordionElementItem
          activeRt={isActiveElement && activeIndex === index.toString()}
          block={block}
          element={element}
          font={font}
          index={index}
          isOpen={state?.open?.[item.value]}
          item={item}
          key={item.value}
          mode={mode}
          onChange={onChange}
          onChoose={handleChoose}
          onLabelSelect={onLabelSelect}
          onToggle={handleToggle}
          styles={styles}
          waiting={waiting}
        />
      ))}
    </RootStyled>
  )
}

export default React.memo(AccordionElement) as typeof AccordionElement
